import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchRegistrants } from '@/store/slices/registrants';
import { selectRegistrantsLoading } from '@/store/slices/registrants/selectors';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchMemberPasscodeLists, fetchPasscodeLists } from '@/store/slices/passcode-lists';
import toastTrigger from '@ui/toast/toast-trigger';
import { getStatusQueryParam } from '../default-passcode-lists';
import { REGISTRANT_FILTER_TYPE } from '@/types/registration.types';
import { EToastTypes } from '@ui/toast/toast.types';

export const useLoadRegistrants = (
  eventUuid: string,
  limit: number,
  uploadPasscodeList: number,
  getDeleted?: boolean,
  coursesEnabled?: boolean,
  channel?: number,
  start?: number,
  end?: number
) => {
  const registrantsLoading = useAppSelector(selectRegistrantsLoading);
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const search = searchParams.get('search') || '';
  const pageIndex = parseInt(searchParams.get('page') || '1') - 1 || 0;
  const offset = pageIndex * limit;
  const sort = searchParams.get('sort') || '';
  const lists = searchParams.get('lists') || '';
  const statusParam = searchParams.get('status') || '';

  // convert status enum values to correct API query parameters
  const status =
    statusParam && Object.values(REGISTRANT_FILTER_TYPE).includes(statusParam as REGISTRANT_FILTER_TYPE)
      ? getStatusQueryParam(statusParam as REGISTRANT_FILTER_TYPE)
      : statusParam;

  const loadRegistrants = useCallback(async () => {
    if (!eventUuid || registrantsLoading) return;

    try {
      const result = await dispatch(
        fetchRegistrants({
          eventUuid,
          offset,
          limit,
          uploadPasscodeList,
          search,
          getDeleted,
          coursesEnabled,
          sort,
          lists,
          status,
          start,
          end,
        })
      ).unwrap();

      // load passcode lists and member associations when registrants are loaded
      if (channel && result && 'registrations' in result) {
        const payload = result as { registrations?: Array<{ email: string }> };
        if (payload?.registrations && Array.isArray(payload.registrations)) {
          await dispatch(fetchPasscodeLists(channel));
          const registrantEmails = payload.registrations.map((r) => r.email);
          await dispatch(fetchMemberPasscodeLists({ channel, emails: registrantEmails }));
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      toastTrigger('Error loading registrants', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coursesEnabled,
    dispatch,
    eventUuid,
    getDeleted,
    limit,
    lists,
    offset,
    search,
    sort,
    statusParam,
    uploadPasscodeList,
    channel,
    start,
  ]);

  return { loadRegistrants, registrantsLoading };
};
